import apiClient, { getApiEndpoint } from '@/lib/apiClient';
import { handleApiError } from '@/lib/apiErrorUtils';
import {
  ContractSearchRequest,
  ContractSearchResult,
  AutocompleteRequest,
  AutocompleteResponse,
  ContractDetail,
} from './types';

/**
 * Searches for contracts based on the provided search criteria.
 *
 * This function queries the contracts API with the given search parameters
 * and returns matching contracts. It ensures the response always contains
 * a contracts array and transforms product type codes to user-friendly names.
 *
 * @param {ContractSearchRequest} request - The search criteria for finding contracts
 * @returns {Promise<ContractSearchResult>} A promise that resolves to search results containing matching contracts
 * @throws {Error} Throws an error with a user-friendly message if the API request fails
 */
export async function searchContracts(
  request: ContractSearchRequest
): Promise<ContractSearchResult> {
  try {
    const endpoint = getApiEndpoint('/contracts/search');
    const response = await apiClient.get<ContractSearchResult>(endpoint, {
      params: request,
    });

    // Ensure that if status is 'success', contracts is always an array.
    if (response.data.status === 'success' && !response.data.contracts) {
      return {
        ...response.data,
        contracts: [],
      };
    }

    const contracts = response.data.contracts.map((contract) => ({
      ...contract,
      // Transform the product type code to a product type name
      productTypeName: transformProductTypeName(contract),
    }));

    return {
      ...response.data,
      contracts,
    };
  } catch (err) {
    const userFriendlyMessage = handleApiError(
      err,
      'searching contracts',
      request
    );
    throw new Error(userFriendlyMessage);
  }
}

/**
 * Retrieves autocomplete suggestions for customer names based on VIN and partial query.
 *
 * This function provides name suggestions for contract search by querying the
 * autocomplete API with a VIN number and partial name query. It ensures the
 * response always contains a suggestions array even if the API returns null.
 *
 * @param {AutocompleteRequest} request - The autocomplete request containing VIN and query string
 * @param {string} request.vin - The vehicle identification number to search against
 * @param {string} request.query - The partial name query to get suggestions for
 * @returns {Promise<AutocompleteResponse>} A promise that resolves to autocomplete suggestions
 * @throws {Error} Throws an error with a user-friendly message if the API request fails
 */
export async function getAutocompleteNames(
  request: AutocompleteRequest
): Promise<AutocompleteResponse> {
  try {
    const endpoint = getApiEndpoint('/contracts/vin/name/autocomplete');
    const response = await apiClient.get<AutocompleteResponse>(endpoint, {
      params: {
        vin: request.vin,
        query: request.query,
      },
    });

    // Ensure suggestions is always an array
    if (!response.data.suggestions) {
      return {
        suggestions: [],
      };
    }

    return response.data;
  } catch (err) {
    const userFriendlyMessage = handleApiError(
      err,
      'fetching autocomplete suggestions',
      request
    );
    throw new Error(userFriendlyMessage);
  }
}

/**
 * Transforms raw product type codes into user-friendly product type names.
 *
 * This function converts internal product type codes from the API into
 * readable product names for display in the user interface. It provides
 * specific mappings for known product types and falls back to the original
 * product type name for unknown codes.
 *
 * @param {ContractDetail} contract - The contract containing product type information
 * @returns {string} A user-friendly product type name
 *
 * @internal This function is used internally by searchContracts
 */
const transformProductTypeName = (contract: ContractDetail) => {
  switch (contract.productTypeCode) {
    case 'VSC':
      return 'Vehicle Service Contract';
    case 'MNT':
      return 'Prepaid Maintenance';
    default:
      return contract.productTypeName;
  }
};
